// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { CHAIR_SIZE_PX } from '../../constants.ts';
import {
  fetchMesasPublic,
  fetchBloqueiosPublic,
  fetchHorariosPublic,
  BloqueioResponse
} from '../../services/api.ts';
import { formatDateToLocalISO, getReservoDayOfWeek } from '../../utils/date.ts';
import { usesTableMap } from '../../utils/roomMode.ts';
import '../../styles/table-selector.css';
import { toast } from 'react-toastify';

const DEFAULT_BLOCK_MESSAGE = 'Este ambiente está indisponível para reservas neste horário.';

const timeStringToMinutes = (time?: string | null): number => {
  if (!time) return Number.NaN;
  const [hoursString, minutesString] = time.split(':');
  const hours = Number(hoursString);
  const minutes = Number(minutesString);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return Number.NaN;
  }
  return hours * 60 + minutes;
};

const isMinuteWithinRange = (target: number, start: number, end: number) => {
  if (Number.isNaN(target) || Number.isNaN(start) || Number.isNaN(end)) {
    return false;
  }

  if (end === start) {
    return target === start;
  }

  if (end > start) {
    return target >= start && target < end;
  }

  // Range that wraps past midnight
  return target >= start || target < end;
};

const blockCoversSelectedSlot = (
  block: BloqueioResponse,
  selectedMinutes: number,
  selectedTurno: number | null,
  selectedHorarioId: number | null
) => {
  const blockTurno = block.turno !== null && block.turno !== undefined ? Number(block.turno) : null;
  const blockHorario = block.horario_id !== null && block.horario_id !== undefined ? Number(block.horario_id) : null;

  const matchesHorario = selectedHorarioId !== null && blockHorario !== null && blockHorario === selectedHorarioId;
  const matchesTurno = selectedTurno !== null && blockTurno !== null && blockTurno === selectedTurno;
  const isGeneral = blockHorario === null && blockTurno === null;

  let matchesRange = false;
  const rangeStart = timeStringToMinutes(block.hora_inicio);
  const rangeEnd = timeStringToMinutes(block.hora_fim);
  if (!Number.isNaN(rangeStart) && !Number.isNaN(rangeEnd)) {
    matchesRange = isMinuteWithinRange(selectedMinutes, rangeStart, rangeEnd);
  }

  return matchesHorario || matchesTurno || isGeneral || matchesRange;
};

interface TableSelectorProps {
  selectedDate: Date | null;
  selectedTime: string;
  partySize: number;
  selectedTableIds: string[];
  restaurantSlug?: string;
  legacyMapDefault?: boolean;
  onTableSelect: (tableIds: string[]) => void;
  onNext: () => void;
  onBack: () => void;
}

const TableSelector: React.FC<TableSelectorProps> = ({
  selectedDate,
  selectedTime,
  partySize,
  selectedTableIds,
  restaurantSlug = 'gourmex',
  legacyMapDefault = true,
  onTableSelect,
  onNext,
  onBack
}) => {
  const { floors, currentFloor, setCurrentFloor } = useRestaurant();
  
  const [mesasData, setMesasData] = useState<any[]>([]);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [loadingMesas, setLoadingMesas] = useState(false);
  const [nonLayoutConfirmed, setNonLayoutConfirmed] = useState(false);
  const [blockedFloorMessages, setBlockedFloorMessages] = useState<Record<string, string>>({});

  const blockedFloorIds = useMemo(() => new Set(Object.keys(blockedFloorMessages)), [blockedFloorMessages]);
  const selectedDateKey = selectedDate ? formatDateToLocalISO(selectedDate) : null;

  
  useEffect(() => {
    const fetchMesasData = async () => {
      if (!selectedDate) {
        setMesasData([]);
        return;
      }

      try {
        setLoadingMesas(true);
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;

        const mesas = await fetchMesasPublic(restaurantSlug, dateString, selectedTime, partySize);
        setMesasData(mesas);
      } catch (error) {
        console.error('Erro ao buscar mesas:', error);
        setMesasData([]);
      } finally {
        setLoadingMesas(false);
      }
    };
    fetchMesasData();
  }, [selectedDate, selectedTime, partySize, currentFloor, restaurantSlug]);

  useEffect(() => {
    if (!selectedDateKey || !selectedTime || floors.length <= 1) {
      setBlockedFloorMessages({});
      return;
    }

    let isCancelled = false;

    const evaluateBlockedFloors = async () => {
      try {
        const [bloqueios, horarios] = await Promise.all([
          fetchBloqueiosPublic(restaurantSlug),
          fetchHorariosPublic(restaurantSlug)
        ]);

        const dayBlocks = (bloqueios || []).filter(block => block && block.data === selectedDateKey);
        if (dayBlocks.length === 0) {
          if (!isCancelled) {
            setBlockedFloorMessages({});
          }
          return;
        }

        const dayOfWeek = selectedDate ? getReservoDayOfWeek(selectedDate) : null;
        const dayHorarios = Array.isArray(horarios) && dayOfWeek !== null
          ? horarios.filter((horario: any) =>
              horario &&
              horario.dia_semana === dayOfWeek &&
              horario.aberto
            )
          : [];

        const selectedMinutes = timeStringToMinutes(selectedTime);
        const matchingHorario = dayHorarios.find((horario: any) => {
          const start = timeStringToMinutes(horario?.hora_inicio);
          const end = timeStringToMinutes(horario?.hora_fim);
          return isMinuteWithinRange(selectedMinutes, start, end);
        });

        const selectedHorarioId = matchingHorario && matchingHorario.id !== undefined
          ? Number(matchingHorario.id)
          : null;
        const selectedTurno = matchingHorario && matchingHorario.turno !== undefined
          ? Number(matchingHorario.turno)
          : null;

        const blockedMap: Record<string, string> = {};

        dayBlocks.forEach(block => {
          if (!block || !blockCoversSelectedSlot(block, selectedMinutes, selectedTurno, selectedHorarioId)) {
            return;
          }

          const message = block.motivo?.trim() || DEFAULT_BLOCK_MESSAGE;

          if (block.sala_id === null || block.sala_id === undefined) {
            floors.forEach(floor => {
              blockedMap[String(floor.id)] = message;
            });
          } else {
            blockedMap[String(block.sala_id)] = message;
          }
        });

        if (!isCancelled) {
          setBlockedFloorMessages(blockedMap);
        }
      } catch (error) {
        console.error('Erro ao verificar bloqueios das salas:', error);
        if (!isCancelled) {
          setBlockedFloorMessages({});
        }
      }
    };

    evaluateBlockedFloors();

    return () => {
      isCancelled = true;
    };
  }, [selectedDateKey, selectedTime, floors, restaurantSlug, selectedDate]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!(e.target as HTMLElement).closest('.zoom-controls')) {
      e.preventDefault();
    }
    setIsDragging(true);
    setStartPos({
      x: e.touches[0].clientX - position.x,
      y: e.touches[0].clientY - position.y
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    if (!(e.target as HTMLElement).closest('.zoom-controls')) {
      e.preventDefault();
    }
    setPosition({
      x: e.touches[0].clientX - startPos.x,
      y: e.touches[0].clientY - startPos.y
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    setPosition({
      x: e.clientX - startPos.x,
      y: e.clientY - startPos.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoom = (delta: number) => {
    setScale(prev => Math.max(0.5, Math.min(2, prev + delta)));
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setScale(prev => Math.max(0.5, Math.min(2, prev + delta)));
  };
  
  const handleTableClick = (tableId: string) => {
    if (currentFloorId && blockedFloorIds.has(String(currentFloorId))) {
      return;
    }
    const mesa = mesasData.find(m => m.id === tableId);
    if (mesa && mesa.disponivel) {
      const normalizedTableId = String(tableId);
      const nextTableIds = selectedTableIds.some((currentTableId) => String(currentTableId) === normalizedTableId)
        ? selectedTableIds.filter((currentTableId) => String(currentTableId) !== normalizedTableId)
        : [...selectedTableIds, normalizedTableId];
      onTableSelect(nextTableIds);
    }
  };

  const getChairPositions = (table: any) => {
    const chairs: { x: number; y: number; rotation: number }[] = [];
    const { shape, maxCovers } = table;
    
    if (shape.type === 'square' || shape.type === 'rectangle') {
      const dist = [0, 0, 0, 0];
      let remaining = maxCovers;
      for (let i = 0; i < 4; i++) {
        dist[i] = Math.floor(maxCovers / 4);
        remaining -= dist[i];
      }
      for (let i = 0; remaining > 0; i++) {
        dist[i % 4] += 1;
        remaining--;
      }

      const half = CHAIR_SIZE_PX / 2;

      if (dist[0] > 0) {
        const seg = shape.width / dist[0];
        for (let i = 0; i < dist[0]; i++) {
          chairs.push({ x: seg * (i + 0.5) - half, y: -CHAIR_SIZE_PX, rotation: 0 });
        }
      }
      if (dist[1] > 0) {
        const seg = shape.width / dist[1];
        for (let i = 0; i < dist[1]; i++) {
          chairs.push({ x: seg * (i + 0.5) - half, y: shape.height, rotation: 180 });
        }
      }
      if (dist[2] > 0) {
        const seg = shape.height / dist[2];
        for (let i = 0; i < dist[2]; i++) {
          chairs.push({ x: -CHAIR_SIZE_PX, y: seg * (i + 0.5) - half, rotation: 270 });
        }
      }
      if (dist[3] > 0) {
        const seg = shape.height / dist[3];
        for (let i = 0; i < dist[3]; i++) {
          chairs.push({ x: shape.width, y: seg * (i + 0.5) - half, rotation: 90 });
        }
      }
    } else if (shape.type === 'circle') {
      const radius = shape.width / 2;
      const half = CHAIR_SIZE_PX / 2;
      const dist = radius + half;
      for (let i = 0; i < maxCovers; i++) {
        const angle = (i * 2 * Math.PI) / maxCovers;
        chairs.push({
          x: radius + dist * Math.cos(angle) - half,
          y: radius + dist * Math.sin(angle) - half,
          rotation: angle * 180 / Math.PI + 90
        });
      }
    }
    
    return chairs;
  };
  
  const currentFloorData = floors.find(floor => String(floor.id) === String(currentFloor));
  const currentFloorId = currentFloorData?.id ?? null;
  const currentFloorUsesMap = usesTableMap(currentFloorData, legacyMapDefault);
  const currentFloorHasLayout = currentFloorUsesMap && currentFloorData?.modo_layout !== false;
  const isNonLayoutFloor = !currentFloorHasLayout;
  const isBlockedFloor = currentFloorId !== null && blockedFloorIds.has(String(currentFloorId));
  const blockedFloorMessage = currentFloorId !== null
    ? blockedFloorMessages[String(currentFloorId)]
    : undefined;
  const displayedBlockedMessage = blockedFloorMessage && blockedFloorMessage.trim()
    ? blockedFloorMessage.trim()
    : DEFAULT_BLOCK_MESSAGE;

  const previousFloorRef = useRef<string | null>(null);

  useEffect(() => {
    if (previousFloorRef.current !== currentFloorId) {
      setNonLayoutConfirmed(false);
      onTableSelect([]);
      previousFloorRef.current = currentFloorId;
    }
  }, [currentFloorId, onTableSelect]);

  useEffect(() => {
    if (!currentFloorId) return;
    const key = String(currentFloorId);
    if (!blockedFloorMessages[key]) {
      return;
    }

    if (selectedTableIds.length > 0) {
      onTableSelect([]);
    }

    if (nonLayoutConfirmed) {
      setNonLayoutConfirmed(false);
    }
  }, [blockedFloorMessages, currentFloorId, onTableSelect, selectedTableIds, nonLayoutConfirmed]);

  useEffect(() => {
    if (!floors.length) {
      return;
    }

    const hasCurrentFloor = currentFloorId !== null && currentFloorId !== undefined;
    if (hasCurrentFloor && !blockedFloorIds.has(String(currentFloorId))) {
      return;
    }

    const fallbackFloor = floors.find(floor => !blockedFloorIds.has(String(floor.id)));
    if (fallbackFloor) {
      if (String(currentFloor) !== String(fallbackFloor.id)) {
        setCurrentFloor(fallbackFloor.id);
      }
      return;
    }

    if (!hasCurrentFloor && floors[0]) {
      setCurrentFloor(floors[0].id);
    }
  }, [floors, blockedFloorIds, currentFloor, currentFloorId, setCurrentFloor]);

  const noTablesAvailable = useMemo(() => {
    if (isNonLayoutFloor || loadingMesas || !mesasData.length) return false;
    return mesasData.every(mesa => !mesa.disponivel);
  }, [isNonLayoutFloor, loadingMesas, mesasData]);

  const fullyUnavailableMessage = useMemo(() => {
    if (isBlockedFloor || isNonLayoutFloor || !noTablesAvailable) return '';
    if (!selectedTime || !selectedDate) {
      return 'Selecione um horário para verificar a disponibilidade das mesas.';
    }
    return 'Nenhuma mesa está disponível para este horário com o tamanho do grupo informado. Ajuste o horário ou a quantidade de pessoas para continuar.';
  }, [isBlockedFloor, isNonLayoutFloor, noTablesAvailable, selectedTime, selectedDate]);

  const canProceed = !isBlockedFloor && (isNonLayoutFloor ? nonLayoutConfirmed : selectedTableIds.length > 0);
  const floorWidth = currentFloorData?.width ?? 800;
  const floorHeight = currentFloorData?.height ?? 600;
  
  return (
    <div className="table-selector">
      <div className="selector-header">
        <p className="reservation-details">
          {selectedDate ? selectedDate.toLocaleDateString('pt-BR', {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
          }) : 'Selecione uma data'} às {selectedTime} • {partySize} {partySize === 1 ? 'pessoa' : 'pessoas'}
          {loadingMesas && <span style={{ marginLeft: '10px', color: '#666' }}>(Carregando mesas...)</span>}
        </p>
      </div>
      
      <div className="floor-tabs">
        {floors.map(floor => (
          <button
            key={floor.id}
            className={`floor-tab ${String(currentFloor) === String(floor.id) ? 'active' : ''} ${blockedFloorIds.has(String(floor.id)) ? 'blocked' : ''}`}
            title={blockedFloorIds.has(String(floor.id)) ? 'Ambiente indisponível neste horário' : undefined}
            onClick={() => {
              const floorKey = String(floor.id);
              if (blockedFloorIds.has(floorKey)) {
                const message = blockedFloorMessages[floorKey]?.trim() || DEFAULT_BLOCK_MESSAGE;
                toast.info(message);
                return;
              }
              setCurrentFloor(floor.id);
            }}
          >
            {floor.name}
          </button>
        ))}
      </div>

      {isBlockedFloor && (
        <div className="table-availability-alert blocked" role="status">
          {displayedBlockedMessage}
        </div>
      )}

      {!isBlockedFloor && isNonLayoutFloor && currentFloorData && (
        <div className="non-layout-panel">
          <div className="non-layout-info">
            <div className="non-layout-icon">
              <Sparkles size={20} />
            </div>
            <div>
              <h3>Reserva rápida neste ambiente</h3>
              <p>
                {currentFloorUsesMap
                  ? <>Escolheremos automaticamente a melhor mesa disponível na sala <strong>{currentFloorData.name}</strong>.</>
                  : <>Sua reserva será feita pela capacidade disponível em <strong>{currentFloorData.name}</strong>.</>}
              </p>
            </div>
          </div>
          <label className="non-layout-checkbox">
            <input
              type="checkbox"
              checked={nonLayoutConfirmed}
              onChange={(event) => setNonLayoutConfirmed(event.target.checked)}
            />
            <span>{currentFloorUsesMap
              ? 'Quero reservar automaticamente uma mesa disponível aqui.'
              : 'Quero reservar neste ambiente.'}</span>
          </label>
        </div>
      )}

      {!isBlockedFloor && !isNonLayoutFloor && fullyUnavailableMessage && (
        <div className="table-availability-alert" role="status">
          {fullyUnavailableMessage}
        </div>
      )}

      {!isBlockedFloor && !isNonLayoutFloor && (
      <div
        className="floor-view-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <div className="zoom-controls" style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: '5px'
        }}>
          <button
            onClick={() => handleZoom(0.1)}
            style={{
              width: '30px',
              height: '30px',
              border: '1px solid #a58d72',
              backgroundColor: '#f5f5f5',
              color: '#333',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >+</button>
          <button
            onClick={() => handleZoom(-0.1)}
            style={{
              width: '30px',
              height: '30px',
              border: '1px solid #a58d72',
              backgroundColor: '#f5f5f5',
              color: '#333',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >-</button>
        </div>
        <div
          className="floor-view"
          style={{
            backgroundImage: currentFloorData ? `url(/textures/${currentFloorData.texture}.jpg)` : 'none',
            backgroundRepeat: 'repeat',
            backgroundSize: 'auto',
            width: `${floorWidth}px`,
            height: `${floorHeight}px`,
            minWidth: `${floorWidth}px`,
            minHeight: `${floorHeight}px`,
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: '0 0',
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
        >
          {mesasData.map(mesa => {
            // Find corresponding table data from floors for visual properties
            const tableData = currentFloorData?.tables.find(t => t.id === mesa.id);
            if (!tableData) return null;

            const isAvailable = mesa.disponivel;
            const isSelected = selectedTableIds.some((tableId) => String(tableId) === String(mesa.id));
            const chairs = getChairPositions(tableData);
            const statusText = isAvailable ? 'Vago' : (mesa.motivo_indisponibilidade || 'Ocupado');
            const statusClassName = isAvailable ? 'available' : 'unavailable';
            //const minCapacity = mesa.min ?? tableData?.minCovers ?? '?';
            //const maxCapacity = mesa.max ?? tableData?.maxCovers ?? '?';

            const tableStyle: React.CSSProperties = {
              position: 'absolute',
              left: `${tableData.position.x - tableData.shape.width / 2}px`,
              top: `${tableData.position.y - tableData.shape.height / 2}px`,
              width: `${tableData.shape.width}px`,
              height: `${tableData.shape.height}px`,
              borderRadius: tableData.shape.type === 'circle' ? '50%' : '4px',
              transform: `rotate(${tableData.rotation}deg)`,
              opacity: isAvailable ? 1 : 0.5,
              border: isSelected ? '3px solid #4caf50' : '1px solid #a58d72',
              cursor: isAvailable ? 'pointer' : 'not-allowed',
              ...(tableData.useColor ? {
                backgroundColor: tableData.color || '#8B4513'
              } : {
                backgroundImage: `url(/textures/${tableData.texture}.jpg)`
              })
            };

            return (
              <div
                key={mesa.id}
                className={`public-table ${isAvailable ? 'available' : 'unavailable'} ${isSelected ? 'selected' : ''}`}
                style={tableStyle}
                onClick={() => isAvailable && handleTableClick(mesa.id)}
              >
                <div className="table-info">

                  <span className={`table-status ${statusClassName}`}>
                    {statusText}
                  </span>
                </div>
                {chairs.map((chair, index) => (
                  <div
                    key={index}
                    className="chair"
                    style={{
                      left: chair.x,
                      top: chair.y,
                      transform: `rotate(${chair.rotation}deg)`,
                      width: CHAIR_SIZE_PX,
                      height: CHAIR_SIZE_PX,
                      backgroundColor: tableData.chairColor || '#8B4513'
                    }}
                  ></div>
                ))}
              </div>
            );
          })}
          
          {currentFloorData && currentFloorData.elements.map(element => {
            const elementStyle: React.CSSProperties = {
              position: 'absolute',
              left: `${element.position.x - element.width / 2}px`,
              top: `${element.position.y - element.height / 2}px`,
              width: `${element.width}px`,
              height: `${element.height}px`,
              borderRadius: element.shape === 'circle' ? '50%' : '4px',
              transform: `rotate(${element.rotation}deg)`,
              backgroundColor: element.color || (element.type === 'door' ? '#e8d9b5' : '#e9c46a'),
              border: '1px solid #a58d72'
            };
            
            return (
              <div
                key={element.id}
                className="floor-element"
                style={elementStyle}
              >
                <div className="table-info">
                  <span className="table-name" style={{ color: element.textColor || '#000000' }}>{element.name}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      <div className="selector-actions">
        <button className="btn-back" onClick={onBack}>
          <ChevronLeft size={18} />
          Voltar
        </button>

        <button 
          className="btn-next" 
          onClick={onNext}
          disabled={!canProceed}
        >
          Continuar
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default TableSelector;
